import { FC, useContext, useState } from 'react';
import './ItemCard.css';
import { resolveImageSrc } from '../../api/client';
import { formatDollars } from '../../shared/functions/formatter';
import { CashierContext } from '../Cashier';

type ItemCardProps = {
  item: Item;
  /** The category's palette2 card background: data, not a theme token. */
  bg: string;
  /** The category's palette3 card text: data, not a theme token. */
  fg: string;
  /** True at phone widths: the compact, whole-card tap target (spec 10-15). */
  compact?: boolean;
};

const ItemCard: FC<ItemCardProps> = ({ item: { img, title, price, id }, bg, fg, compact = false }) => {
  const { state, dispatch } = useContext(CashierContext);
  /** Set once an <img> fails, so the tile falls back to a named placeholder. */
  const [imgFailed, setImgFailed] = useState(false);
  /** Units of this product already in the order; drives the phone badge. */
  const quantity = state.items.find((entry) => entry.id === id)?.quantity ?? 0;

  const addItem = () => dispatch({ type: 'ADD', payload: { img, title, price, id } });

  if (compact) {
    /*
     * Spec 12-15: on a phone the WHOLE card is the add action and there is no
     * separate "+ Add" button, so the card is the single interactive element -
     * a button role with keyboard activation, a visible focus ring, and an
     * accessible name that carries the product, the price, the action and the
     * quantity (so the badge is not sighted-only). The quantity badge is
     * decorative markup inside that same target; it is never its own control.
     *
     * A tile with no image, or one the device has never cached (first-ever
     * offline), renders a placeholder carrying the item name - never a broken
     * image icon. The image path keeps no query string so the CacheFirst runtime
     * cache in vite.config.ts still matches it.
     */
    const label =
      quantity > 0
        ? `${formatDollars(price)} ${title} - add to order, ${quantity} in order`
        : `${formatDollars(price)} ${title} - add to order`;

    return (
      <div
        className="itemCard itemCardCompact"
        style={{ backgroundColor: bg }}
        role="button"
        tabIndex={0}
        aria-label={label}
        onClick={addItem}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            addItem();
          }
        }}
      >
        <div className="itemCardMedia">
          {!img || imgFailed ? (
            <div className="itemCardPlaceholder" aria-hidden="true">
              {title}
            </div>
          ) : (
            <img src={resolveImageSrc(img)} alt="" onError={() => setImgFailed(true)} />
          )}
          {/* Spec 11/15: quantity in the order, only when > 0. */}
          {quantity > 0 && (
            <span className="itemQtyBadge" aria-hidden="true">
              {quantity}
            </span>
          )}
        </div>
        <div className="itemNameAndPrice">
          <div className="name" style={{ color: fg }}>
            {title}
          </div>
          {/* Item.price is already dollars at the API edge, so formatDollars is
              the SGD formatter here; formatMoney takes cents and would divide by
              100. Same data ink as the name above: the pad keeps its colour in
              both themes, so a theme token scored 1.72:1 in dark. */}
          <div className="price money" style={{ color: fg }}>
            {formatDollars(price)}
          </div>
        </div>
      </div>
    );
  }

  return (
    /*
     * The desktop tile is the till's primary control: an ARIA button reachable
     * by Tab and fired by Enter or Space. The photo is decorative - the name
     * beside it is what the control is called - so it is hidden from the
     * accessible name. Desktop keeps this exact DOM (spec 53).
     */
    <div
      className="itemCard"
      style={{ backgroundColor: bg }}
      role="button"
      tabIndex={0}
      onClick={addItem}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          addItem();
        }
      }}
    >
      <img src={resolveImageSrc(img)} alt="" />
      <div className="itemNameAndPrice">
        <div className="name" style={{ color: fg }}>
          {title}
        </div>
        {/* Same data ink as the name above: the pad keeps its colour in both
            themes, so a theme token scored 1.72:1 in dark. */}
        <div className="price money" style={{ color: fg }}>
          {formatDollars(price)}
        </div>
      </div>
    </div>
  );
};

export default ItemCard;
